import { css } from "@linaria/core";
import * as io from "io-ts";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import useActiveAnalysesStream from "../../../../../../../../api/useActiveAnalysesStream";
import type { Project } from "../../../../../../../../api/useProjects";
import t from "../../../../../../../../i18n";
import { nullable } from "diagnostics-lib/io";
import ButtonIconV2 from "@ui/ButtonIconV2";
import formatDate from "@ui/i18n/formatDate";
import { ArrowLeftSLine, ArrowRightSLine } from "@ui/IconV2/react";
import createGlobalState from "@ui/lib/createGlobalState";
import Select from "@ui/Select";
import { spacings } from "@ui/Theme";
import type { BlockDiagramModelT } from "../../../../../schema/block/diagram";
import { indexBy } from "diagnostics-lib/iterable";
const useGlobalAnnotationAnalysisDate = createGlobalState({
  key: "DiagramAnnotationsAnalysisDate",
  storage: "local",
  scoped: true,
  type: nullable(io.type({ id: io.string, date: io.number })),
});

export function useAnnotationAnalysis(block: BlockDiagramModelT, projectId: string) {
  const { activeAnalyses } = useActiveAnalysesStream(projectId);
  const latestActiveAnalysisDate = useMemo(() => {
    const latest = activeAnalyses[activeAnalyses.length - 1];
    if (!latest) return null;
    return { date: latest.date, id: latest.id };
  }, [activeAnalyses]);

  const state = useGlobalAnnotationAnalysisDate({
    scope: block.id,
    defaultValue: latestActiveAnalysisDate,
  });

  const previousLatestActiveAnalysisDate = useRef(latestActiveAnalysisDate);
  useEffect(() => {
    if (!block.config.live) return;
    if (previousLatestActiveAnalysisDate.current?.date === latestActiveAnalysisDate?.date) return;
    state[1]((previousLatestActiveAnalysisDate.current = latestActiveAnalysisDate));
  }, [block.config.live, latestActiveAnalysisDate, state]);

  return state;
}

export function AnalysisSelector({
  block,
  project,
}: {
  block: BlockDiagramModelT;
  project: Project;
}) {
  const { activeAnalyses } = useActiveAnalysesStream(project.id);
  const [annotationAnalysis, setAnnotationAnalysis] = useAnnotationAnalysis(block, project.id);
  const activeAnalysesByDate = useMemo(() => indexBy(activeAnalyses, "date"), [activeAnalyses]);
  const analysisDates = useMemo(
    () => Array.from(activeAnalysesByDate.keys()),
    [activeAnalysesByDate],
  );

  const analysesOptions = useMemo(
    () =>
      Array.from(activeAnalyses)
        .sort((a, b) => b.date - a.date)
        .map(a => {
          const cycle = a.group
            ? t("commons.analysis_title.group", { group: `${a.group}`.split("-")[0] ?? "" })
            : "";
          const label = a.label ? t("commons.analysis_title.label", { label: `${a.label}` }) : "";
          return {
            label:
              `${cycle} ${label} ${cycle.length > 0 || label.length > 0 ? "—" : ""} ${formatDate(a.date, "datetime", project.timezone)}`.trim(),
            value: a.date.toString(),
          };
        }),
    [activeAnalyses, project.timezone],
  );

  const updateAnalysisDate = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const analysis = activeAnalysesByDate.get(Number(e.target.value));
      if (!analysis) return;
      setAnnotationAnalysis({ date: analysis.date, id: analysis.id });
    },
    [activeAnalysesByDate, setAnnotationAnalysis],
  );

  const moveAnalysisDate = useCallback(
    (dir: 1 | -1) => () => {
      if (!annotationAnalysis) return;
      const i = analysisDates.indexOf(annotationAnalysis.date);
      const l = analysisDates.length;
      const next = Math.min(i + 1, l);
      const prev = Math.max(i - 1, 0);
      const date = dir === 1 ? analysisDates[next] : analysisDates[prev];
      const id = date ? activeAnalysesByDate.get(date)?.id : null;
      if (!date || !id) return;
      setAnnotationAnalysis({ date, id });
    },
    [annotationAnalysis, analysisDates, activeAnalysesByDate, setAnnotationAnalysis],
  );

  return (
    <div className={analysisSelector}>
      <ButtonIconV2
        disabled={
          analysesOptions.length === 0 ||
          annotationAnalysis?.date === Number(analysesOptions[analysesOptions.length - 1]?.value)
        }
        onClick={moveAnalysisDate(-1)}
      >
        <ArrowLeftSLine size="s" />
      </ButtonIconV2>
      <Select
        // label={t("project.dashboard.block.diagram.displayed_analysis")}
        options={analysesOptions}
        prompt=""
        value={annotationAnalysis?.date.toString() ?? undefined}
        onChange={updateAnalysisDate}
        clearable={false}
        filtered
        className={analysisSelect}
        tooltipOverlay={
          block.config.live
            ? t("project.dashboard.block.diagram.displayed_analysis.details")
            : undefined
        }
      />
      <ButtonIconV2
        disabled={
          analysesOptions.length === 0 ||
          annotationAnalysis?.date === Number(analysesOptions[0]?.value)
        }
        onClick={moveAnalysisDate(1)}
      >
        <ArrowRightSLine size="s" />
      </ButtonIconV2>
    </div>
  );
}

const analysisSelector = css`
  display: grid;
  grid-auto-columns: max-content;
  grid-auto-flow: column;
  align-items: center;
  column-gap: ${spacings.s};
  width: 100%;

  &:not(:first-child) {
    margin-top: ${spacings.m};
  }
`;

const analysisSelect = css`
  width: 360px;
`;
